import type { AdminClient } from '../core/index.js'
import type {
  ApproveChangeInput,
  ChangePrompt,
  ChangeRequestView,
  FeedbackItem,
  RecordPullRequestInput,
  Requirement,
  ResolveFeedbackInput,
  SubmitFeedbackInput,
} from './types.js'

/** The feedback module's admin calls: every client's feedback, the admin's own, and the change requests feedback becomes. */
export interface FeedbackAdminApi {
  /** Every feedback item: open ones in priority order, then resolved ones newest first. */
  listFeedback(): Promise<FeedbackItem[]>
  /** Feedback from the admin themselves, recorded against the admin username. */
  submitFeedback(input: SubmitFeedbackInput): Promise<FeedbackItem>
  /** Marks an item complete or discarded (with a reason), or reopens it. */
  resolveFeedback(feedbackId: string, decision: ResolveFeedbackInput): Promise<FeedbackItem>
  /** Sets the priority of the open items named, in that order; answers the open items in their new order. */
  reorderFeedback(feedbackIds: string[]): Promise<FeedbackItem[]>

  /** Every change request, the ones still moving first. */
  listChanges(): Promise<ChangeRequestView[]>
  getChange(changeRequestId: string): Promise<ChangeRequestView>
  /** Gate 1 (any admin): opens a draft change for this open item, and any others named. */
  developFeedback(feedbackId: string, feedbackIds?: string[]): Promise<ChangeRequestView>
  /** A new requirement version while a draft; `version` is the record's as last read (a stale one is a 409). */
  saveRequirement(changeRequestId: string, version: number, requirement: Requirement): Promise<ChangeRequestView>
  submitChange(changeRequestId: string, version: number): Promise<ChangeRequestView>
  /** Root: proposed back to draft. */
  returnChange(changeRequestId: string, reason: string): Promise<ChangeRequestView>
  /** Root, gate 2: approves one version by its hash. */
  approveChange(changeRequestId: string, input: ApproveChangeInput): Promise<ChangeRequestView>
  /** Root: the approved version's prompt. */
  changePrompt(changeRequestId: string): Promise<ChangePrompt>
  /** Root: the pull request that came back, at a commit. */
  recordPullRequest(changeRequestId: string, input: RecordPullRequestInput): Promise<ChangeRequestView>
  /** Root: in review back to approved, with notes. */
  sendBack(changeRequestId: string, notes: string): Promise<ChangeRequestView>
  /** Root, gate 3: accepts the pull request at the commit reviewed. */
  acceptChange(changeRequestId: string, headSha: string): Promise<ChangeRequestView>
  rejectChange(changeRequestId: string, reason: string): Promise<ChangeRequestView>
  cancelChange(changeRequestId: string, reason: string): Promise<ChangeRequestView>
}

const changePath = (changeRequestId: string, step = ''): string => `/admin/changes/${encodeURIComponent(changeRequestId)}${step}`

export function createFeedbackAdminApi(client: AdminClient): FeedbackAdminApi {
  return {
    listFeedback: () => client.call('GET', '/admin/feedback'),
    submitFeedback: (input) => client.call('POST', '/admin/feedback', input),
    resolveFeedback: (feedbackId, decision) => client.call('PATCH', `/admin/feedback/${encodeURIComponent(feedbackId)}`, decision),
    reorderFeedback: (feedbackIds) => client.call('PUT', '/admin/feedback/order', { feedbackIds }),

    listChanges: () => client.call('GET', '/admin/changes'),
    getChange: (changeRequestId) => client.call('GET', changePath(changeRequestId)),
    developFeedback: (feedbackId, feedbackIds = []) => client.call('POST', `/admin/feedback/${encodeURIComponent(feedbackId)}/develop`, { feedbackIds }),
    saveRequirement: (changeRequestId, version, requirement) => client.call('PUT', changePath(changeRequestId, '/requirement'), { version, requirement }),
    submitChange: (changeRequestId, version) => client.call('POST', changePath(changeRequestId, '/submit'), { version }),
    returnChange: (changeRequestId, reason) => client.call('POST', changePath(changeRequestId, '/return'), { reason }),
    approveChange: (changeRequestId, input) => client.call('POST', changePath(changeRequestId, '/approve'), input),
    changePrompt: (changeRequestId) => client.call('GET', changePath(changeRequestId, '/prompt')),
    recordPullRequest: (changeRequestId, input) => client.call('POST', changePath(changeRequestId, '/pull-request'), input),
    sendBack: (changeRequestId, notes) => client.call('POST', changePath(changeRequestId, '/send-back'), { notes }),
    acceptChange: (changeRequestId, headSha) => client.call('POST', changePath(changeRequestId, '/accept'), { headSha }),
    rejectChange: (changeRequestId, reason) => client.call('POST', changePath(changeRequestId, '/reject'), { reason }),
    cancelChange: (changeRequestId, reason) => client.call('POST', changePath(changeRequestId, '/cancel'), { reason }),
  }
}
